import {
  Button,
  Spin,
  DatePicker,
  ConfigProvider,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Typography,
  message,
} from "antd";
import React, { forwardRef, useImperativeHandle, useState } from "react";
import {
  DeleteOutlined,
  EditOutlined,
  HomeOutlined,
  PlusOutlined,
  TeamOutlined,
  CreditCardOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import http from "@/utils/http";
import api from "@/utils/api";
import commonFn from "@/utils/commonFn";

  const YES_NO_OPTIONS = [
    { value: "Yes", label: "Yes" },
    { value: "No", label: "No" },
  ];

  const RENTAL_PROP_OPTIONS = [
    { value: " ", label: " " },
    { value: "0", label: "Yes(Rental Property)" },
    { value: "1", label: "No(Not a Property)" },
  ];

  const STATUS_OPTIONS = [
    { value: "P", label: "Active" },
    { value: "X", label: "Inactive" },
  ];

  const LOCAL_OFFICE_OPTIONS = [
    { value: 1, label: "Yes" },
    { value: 0, label: "No" },
  ];

  const RENT_TYPE_OPTIONS = [
    { value: "none", label: "None" },
    { value: "rent control", label: "Rent Control" },
  ];
  const MAN_ON_SITE_OPTIONS = [
    { value: "1", label: "Yes" },
    { value: "0", label: "No" },
  ];
  // ---------- 卡片样式 ----------
  const sectionCardStyle = {
    flex: 1,
    minWidth: 420,
    border: "1px solid #e5e7eb",
    borderRadius: 6,
    background: "#fff",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    overflow: "hidden",
    alignSelf: "flex-start",
  };

  const stackedCardStyle = {
    flex: "none",
    minWidth: 0,
    alignSelf: "stretch",
  };

  const sectionHeaderStyle = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 20px",
    background: "#f7f8fa",
    borderBottom: "1px solid #e5e7eb",
    fontSize: 18,
  };

  const sectionBodyStyle = { padding: "16px 20px 0" };


function useLazySelect(apiUrl, key, mapper, enableCache = true) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const load = async () => {
    if (enableCache && loaded) return;
    setLoading(true);
    try {
      const res = await http.get(apiUrl);
      if (res?.code === "1") {
        const list = res?.data?.[key] || res?.data || [];
        setOptions(mapper(list));
        setLoaded(true);
      } else {
        setOptions([]);
      }
    } catch (err) {
      console.error("Select load error:", err);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  return { options, loading, load };
}

const EditModal = forwardRef((props, ref) => {
  const { onSuccess } = props;
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [openLoading, setOpenLoading] = useState(false);
  const [bankOptions, setBankOptions] = useState([]);
  const [bankLoading, setBankLoading] = useState(false);
  const [countyOptions, setCountyOptions] = useState([]);
  const [countyLoading, setCountyLoading] = useState(false);
  const [cityOptions, setCityOptions] = useState([]);
  const [cityLoading, setCityLoading] = useState(false);
  // const stateSelect = useLazySelect(api.getStateDict, "state", (data) =>
  //   (data || []).map((item) => ({
  //     label: item?.label ?? item?.value ?? item,
  //     value: item?.label ?? item?.value ?? item,
  //   })),
  // );
  // 更换接口
  const stateSelect = useLazySelect(api.getCountyStateDict, "state", (data) =>
    (data || []).map((item) => ({
      label: item?.label,
      value: item?.value,
      id: item?.value,
    })),
  );
  const propTypeSelect = useLazySelect(api.getPropTypeList, null, (data) =>
    (data || []).map((item) => ({
      label: item?.name ?? item?.code ?? String(item?.id ?? ""),
      value: item?.name ?? item?.code ?? String(item?.id ?? ""),
    })),
  );

  const companySelect = useLazySelect(api.getOwnerDictList, "company", (data) =>
    (data || []).map((item) => ({
      label: item?.titleName,
      value: item?.ownerId,
    })),
  );

  const groupSelect = useLazySelect(api.commonGroupList, "group", (data) =>
    (data || []).map((item) => ({
      label: item?.geographyGroup ?? item?.groupCode,
      value: item?.groupId,
    })),
  );

  const trustSelect = useLazySelect(api.getTrustDictList, "trust", (data) =>
    (data || []).map((item) => ({
      label: item?.trustNameTitle ?? item?.trustName ?? item?.label ?? "",
      value: String(item?.trustId ?? item?.value ?? ""),
    })),
  );

  // 根据 stateId 查 County
  const loadCountyOptions = async (stateId) => {
    if (stateId === undefined || stateId === null || stateId === "") {
      setCountyOptions([]);
      return;
    }
    setCountyLoading(true);
    try {
      const res = await http.get(api.getCountyDictForId, {
        params: { stateId },
      });
      if (res?.code !== "1") {
        setCountyOptions([]);
        return;
      }
      const list = res?.data?.county || res?.data || [];
      setCountyOptions(
        (list || []).map((item) => ({
          label: item?.label,
          value: item?.value,
        })),
      );
    } catch (err) {
      console.error("County load error:", err);
      setCountyOptions([]);
    } finally {
      setCountyLoading(false);
    }
  };

  // 根据 countyId 查 City
  const loadCityOptions = async (countyId) => {
    if (countyId === undefined || countyId === null || countyId === "") {
      setCityOptions([]);
      return;
    }
    setCityLoading(true);
    try {
      const res = await http.get(api.getCityDictForId, {
        params: { countyId },
      });
      if (res?.code !== "1") {
        setCityOptions([]);
        return;
      }
      const list = res?.data?.city || res?.data || [];
      setCityOptions(
        (list || []).map((item) => ({
          label: item?.label,
          value: item?.value,
        })),
      );
    } catch (err) {
      console.error("City load error:", err);
      setCityOptions([]);
    } finally {
      setCityLoading(false);
    }
  };

  // State 变化清空 county / city，并重新加载 county
  const handleStateChange = async (stateId) => {
    form.setFieldsValue({ county: undefined, city: undefined });
    setCityOptions([]);
    await loadCountyOptions(stateId);
  };

  // County 变化清空 city，并重新加载 city
  const handleCountyChange = async (countyId) => {
    form.setFieldsValue({ city: undefined });
    await loadCityOptions(countyId);
  };
  const loadBankOptions = async (trustId) => {
    const id = String(trustId ?? "").trim();
    if (!id) {
      setBankOptions([]);
      return;
    }

    setBankLoading(true);
    try {
      const res = await http.get(api.getBankListDict, {
        params: { trustId: id },
      });
      if (res?.code !== "1") {
        setBankOptions([]);
        return;
      }

      const list = res?.data?.bank || res?.data || [];
      setBankOptions(
        (list || []).map((item) => ({
          label: item?.name,
          value: String(item?.bankId),
        })),
      );
    } catch (err) {
      console.error("Bank select load error:", err);
      setBankOptions([]);
    } finally {
      setBankLoading(false);
    }
  };

  const getPropertyDetail = async (propId) => {
    const id = String(propId ?? "").trim();
    if (!id) return null;

    const res = await http.get(api.getProperByIdDetail, {
      params: { propId: id },
    });
    if (res?.code !== "1") return null;

    return res?.data || null;
  };
  const mapTriState = (val) => {
    if (val === 0 || val === "0") return "0";
    if (val === 1 || val === "1") return "1";
    return " ";
  };

  const getFormValuesFromDetail = (detail = {}) => ({
    prop: detail?.prop ?? "",
    propName: detail?.propName ?? "",
    street: detail?.street ?? "",
    city: detail?.city ?? "",
    county: detail?.county ?? "",
    state: detail?.state ?? undefined,
    zip: detail?.zip ?? "",
    propClass: detail?.propClass ?? "P",
    propType: detail?.propType ?? undefined,
    longitude: detail?.longitude ?? "",
    latitude: detail?.latitude ?? "",
    squareFootage: detail?.squareFootage ?? "",
    lotSquareFootage: detail?.lotSquareFootage ?? "",
    remark: detail?.remark ?? "",
    rentalProp: mapTriState(detail?.rentalProp),
    isFreeClear: detail?.isFreeClear ?? detail?.isfreeclear ?? undefined,
    startDate: detail?.startDate ? dayjs(detail.startDate) : undefined,
    taxDate: detail?.taxDate ? dayjs(detail.taxDate) : undefined,
    poValue: detail?.poValue ?? "",
    sqFeet: detail?.sqFeet ?? "",
    taxRate: detail?.taxRate ?? "",
    trustId:
      detail?.trustId === 0 || detail?.trustId
        ? String(detail.trustId)
        : undefined,
    arBankId:
      detail?.arBankId === 0 || detail?.arBankId
        ? String(detail.arBankId)
        : undefined,
    apBankId:
      detail?.apBankId === 0 || detail?.apBankId
        ? String(detail.apBankId)
        : undefined,
    mapCodeList:
      Array.isArray(detail?.mapCodeList) && detail.mapCodeList.length
        ? detail.mapCodeList
        : [""],
    companyId:
      detail?.companyId === 0 || detail?.companyId
        ? detail.companyId
        : undefined,
    groupId:
      detail?.groupId === 0 || detail?.groupId ? detail.groupId : undefined,
    manFlatAmt: detail?.manFlatAmt ?? "",
    manPct: detail?.manPct ?? "",
    line2: detail?.line2 ?? "",
    rentType: detail?.rentType ?? "none",
    phone: detail?.phone ?? "",
    manOnSite: detail?.manOnSite != null ? String(detail.manOnSite) : undefined,
    manFirstName: detail?.manFirstName ?? detail?.firstname ?? "",
    manLastName: detail?.manLastName ?? detail?.lastname ?? "",
    isLocalOffice:
      detail?.isLocalOffice !== undefined && detail?.isLocalOffice !== null
        ? Number(detail.isLocalOffice)
        : undefined,
  });

  useImperativeHandle(ref, () => ({
    setModelAdd: async (record = {}) => {
      form.resetFields();
      setCurrentRecord(record || null);
      setVisible(true);
      setOpenLoading(true);
      try {
        await Promise.all([
          stateSelect.load(),
          propTypeSelect.load(),
          companySelect.load(),
          groupSelect.load(),
          trustSelect.load(),
        ]);
        const detailRecord = await getPropertyDetail(record?.propId);
        if (!detailRecord) {
          message.error("Failed to load property detail.");
          return;
        }
        const nextRecord = {
          ...detailRecord,
          propId: detailRecord?.propId ?? record?.propId,
        };
        await loadBankOptions(nextRecord?.trustId);
        setCurrentRecord(nextRecord);
        form.setFieldsValue(getFormValuesFromDetail(nextRecord));
      } finally {
        setOpenLoading(false);
      }
    },
  }));
  const getLabel = (options, val) =>
    (options || []).find((o) => o.value === val)?.label ?? "";
  const onFinish = async (values) => {
    setLoading(true);
    try {
      const payload = {
        propId: currentRecord?.propId ?? "",
        prop: values?.prop ?? "",
        propName: values?.propName ?? "",
        street: values?.street ?? "",
        // city: values?.city ?? "",
        // county: values?.county ?? "",
        // state: values?.state ?? "",
        city: getLabel(cityOptions, values?.city),
        county: getLabel(countyOptions, values?.county),
        state: getLabel(stateSelect.options, values?.state),
        zip: values?.zip ?? "",
        propClass: values?.propClass ?? "",
        propType: values?.propType ?? "",
        remark: values?.remark ?? "",
        rentalProp: values?.rentalProp ?? "",
        startDate: values?.startDate
          ? dayjs(values.startDate).format("YYYY-MM-DD")
          : "",
        taxDate: values?.taxDate
          ? dayjs(values.taxDate).format("YYYY-MM-DD")
          : "",
        poValue: values?.poValue ?? "",
        squareFootage: values?.squareFootage ?? "",
        lotSquareFootage: values?.lotSquareFootage ?? "",
        sqFeet: values?.sqFeet ?? "",
        taxRate: values?.taxRate ?? "",
        mapCodeList: (values?.mapCodeList || []).filter(
          (v) => String(v || "").trim() !== "",
        ),
        companyId: values?.companyId ?? "",
        groupId: values?.groupId ?? "",
        manFlatAmt: values?.manFlatAmt ?? "",
        manPct: values?.manPct ?? "",
        line2: values?.line2 ?? "",
        rentType: values?.rentType ?? "none",
        phone: values?.phone ?? "",
        manOnSite: values?.manOnSite ?? "",
        manFirstName: values?.manFirstName ?? "",
        manLastName: values?.manLastName ?? "",
        longitude: values?.longitude ?? "",
        latitude: values?.latitude ?? "",
        arBankId: values?.arBankId ?? "",
        apBankId: values?.apBankId ?? "",
        isFreeClear: values?.isFreeClear ?? "",
        rentalProp: values?.rentalProp ?? "",
        isLocalOffice: values?.isLocalOffice ?? "",
      };

      const res = await http.post(api.getProperUpdateAll, payload);
      if (res?.code === "1") {
        message.success(res?.msg);
        setVisible(false);
        form.resetFields();
        setCurrentRecord(null);
        onSuccess && onSuccess();
      } else {
        message.error(res?.msg);
      }
    } catch (err) {
      console.error(err);
      message.error(err?.msg);
    } finally {
      setLoading(false);
    }
  };

  const filterOption = (input, option) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());


  const SectionCard = ({ icon, title, children, style }) => (
    <div style={{ ...sectionCardStyle, ...style }}>
      <div style={sectionHeaderStyle}>
        {icon}
        <Typography.Text strong style={{ fontSize: 18, fontWeight: 700 }}>
          {title}
        </Typography.Text>
      </div>
      <div style={sectionBodyStyle}>{children}</div>
    </div>
  );
  const compactTheme = {
    components: {
      Form: { itemMarginBottom: 12 },
    },
  };

  const searchProps = {
    allowClear: true,
    showSearch: true,
    optionFilterProp: "label",
    filterOption,
  };

  const closeModal = () => {
    setVisible(false);
    form.resetFields();
    setCurrentRecord(null);
    setOpenLoading(false);
  };

  return (
    <ConfigProvider theme={compactTheme}>
      <Modal
        title={
          <Flex justify="flex-start" align="flex-start" className="model_title">
            <EditOutlined className="model_title_mr10" />
            <Typography.Title
              level={4}
              className="mg"
              style={{ fontWeight: 700 }}
            >
              EDIT PROPERTY
            </Typography.Title>
          </Flex>
        }
        width="85%"
        open={visible}
        maskClosable={false}
        destroyOnClose
        onCancel={closeModal}
        footer={[
          <Flex key="footer" justify="center" gap={12}>
            <Button onClick={closeModal}>Close</Button>
            <Button
              type="primary"
              loading={loading}
              onClick={() => form.submit()}
            >
              Submit
            </Button>
          </Flex>,
        ]}
      >
        <div style={{ width: "100%" }}>
          <Spin spinning={openLoading}>
            <Form
              form={form}
              name="createPropertyForm"
              layout="horizontal"
              labelCol={{ style: { width: "210px" } }}
              wrapperCol={{ style: { flex: 1 } }}
              onFinish={onFinish}
            >
              <Flex align="flex-start" justify="space-between" gap={24}>
                {/* ================= 1. Property Detail ================= */}
                <SectionCard icon={<HomeOutlined />} title="Property Detail">
                  <Form.Item name="rentalProp" label="Rental Property">
                    <Select
                      placeholder="Select"
                      options={RENTAL_PROP_OPTIONS}
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item
                    name="prop"
                    label="Property"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Property Number",
                      },
                    ]}
                  >
                    <Input placeholder="Property Number" allowClear readOnly />
                  </Form.Item>

                  <Form.Item
                    name="propName"
                    label="Property Name"
                    rules={[
                      { required: true, message: "Please enter Property Name" },
                    ]}
                  >
                    <Input placeholder="Property Name" allowClear />
                  </Form.Item>

                  <Form.Item
                    name="street"
                    label="Address"
                    rules={[
                      { required: true, message: "Please enter Address" },
                    ]}
                  >
                    <Input placeholder="Address" allowClear />
                  </Form.Item>

                  <Form.Item
                    name="state"
                    label="State"
                    rules={[{ required: true, message: "Please select State" }]}
                  >
                    <Select
                      placeholder="Select State"
                      loading={stateSelect.loading}
                      options={stateSelect.options}
                      onChange={handleStateChange}
                      {...searchProps}
                    />
                  </Form.Item>

                  <Form.Item
                    name="county"
                    label="County"
                    rules={[
                      { required: true, message: "Please select County" },
                    ]}
                  >
                    <Select
                      placeholder="Select County"
                      loading={countyLoading}
                      options={countyOptions}
                      onChange={handleCountyChange}
                      {...searchProps}
                    />
                  </Form.Item>

                  <Form.Item
                    name="city"
                    label="City"
                    rules={[{ required: true, message: "Please select City" }]}
                  >
                    <Select
                      placeholder="Select City"
                      loading={cityLoading}
                      options={cityOptions}
                      {...searchProps}
                    />
                  </Form.Item>

                  <Form.Item
                    name="zip"
                    label="Zip"
                    rules={[
                      { required: true, message: "Zip Code is required" },
                      { validator: commonFn.zipValidator },
                    ]}
                  >
                    <Input placeholder="Zip" allowClear />
                  </Form.Item>

                  <Form.Item
                    name="propClass"
                    label="Status"
                    rules={[
                      { required: true, message: "Please select Status" },
                    ]}
                  >
                    <Select
                      placeholder="Select Status"
                      options={STATUS_OPTIONS}
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item
                    name="propType"
                    label="Property Type"
                    rules={[
                      {
                        required: true,
                        message: "Please select Property Type",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select Property Type"
                      loading={propTypeSelect.loading}
                      options={propTypeSelect.options}
                      {...searchProps}
                    />
                  </Form.Item>

                  <Form.Item name="longitude" label="Longitude">
                    <Input placeholder="Longitude" allowClear />
                  </Form.Item>

                  <Form.Item name="latitude" label="Latitude">
                    <Input placeholder="Latitude" allowClear />
                  </Form.Item>

                  <Form.Item
                    name="squareFootage"
                    label="Total Building(s) S.F."
                  >
                    <Input placeholder="Total Building(s) S.F." allowClear />
                  </Form.Item>

                  <Form.Item name="lotSquareFootage" label="Total Lot(s) S.F.">
                    <Input placeholder="Total Lot(s) S.F." allowClear />
                  </Form.Item>

                  <Form.Item name="remark" label="Remark">
                    <Input placeholder="Remark" allowClear />
                  </Form.Item>

                  <Form.Item
                    name="isLocalOffice"
                    label="Is Local Office"
                    rules={[
                      {
                        required: true,
                        message: "Please select Is Local Office",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select Is Local Office"
                      options={LOCAL_OFFICE_OPTIONS}
                      allowClear
                    />
                  </Form.Item>
                </SectionCard>

                {/* ================= 2. 中间列:Financial + Tax ================= */}
                <div
                  style={{
                    flex: 1,
                    minWidth: 420,
                    display: "flex",
                    flexDirection: "column",
                    gap: 16,
                  }}
                >
                  {/* ---- Financial Detail ---- */}
                  <SectionCard
                    icon={<DollarOutlined />}
                    title="Financial Detail"
                    style={stackedCardStyle}
                  >
                    <Form.Item
                      name="trustId"
                      label="Trust"
                      rules={[
                        { required: true, message: "Please select Trust" },
                      ]}
                    >
                      <Select
                        placeholder="Select Trust"
                        loading={trustSelect.loading}
                        options={trustSelect.options}
                        disabled
                        {...searchProps}
                      />
                    </Form.Item>

                    <Form.Item
                      name="arBankId"
                      label="AR Default Bank"
                      rules={[
                        {
                          required: true,
                          message: "Please select AR Default Bank",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Bank"
                        loading={bankLoading}
                        options={bankOptions}
                        {...searchProps}
                      />
                    </Form.Item>

                    <Form.Item
                      name="apBankId"
                      label="AP Default Bank"
                      rules={[
                        {
                          required: true,
                          message: "Please select AP Default Bank",
                        },
                      ]}
                    >
                      <Select
                        placeholder="Select Bank"
                        loading={bankLoading}
                        options={bankOptions}
                        {...searchProps}
                      />
                    </Form.Item>

                    <Form.Item name="isFreeClear" label="Free & Clear">
                      <Select
                        placeholder="Select"
                        options={YES_NO_OPTIONS}
                        allowClear
                      />
                    </Form.Item>
                  </SectionCard>

                  {/* ---- Tax Assessment ---- */}
                  <SectionCard
                    icon={<CreditCardOutlined />}
                    title="Tax Assessment"
                    style={stackedCardStyle}
                  >
                    <Form.Item
                      name="startDate"
                      label="Purchase Date"
                      rules={[
                        {
                          required: true,
                          message: "Please enter Purchase Date",
                        },
                      ]}
                    >
                      <DatePicker
                        format="YYYY-MM-DD"
                        style={{ width: "100%" }}
                        allowClear
                      />
                    </Form.Item>

                    <Form.Item
                      name="poValue"
                      label="Purchase Price"
                      rules={[
                        {
                          required: true,
                          message: "Please enter Purchase Price",
                        },
                      ]}
                    >
                      <Input placeholder="Purchase Price" allowClear />
                    </Form.Item>

                    <Form.Item
                      name="taxRate"
                      label="Base Year Assessed Value"
                      normalize={(value) =>
                        (value ?? "").replace(/[^\d.]/g, "")
                      }
                    >
                      <Input
                      prefix="$"
                        placeholder="Base Year Assessed Value"
                        allowClear
                      />
                    </Form.Item>
                    <Form.Item name="taxDate" label="Tax Date">
                      <DatePicker
                        format="YYYY-MM-DD"
                        style={{ width: "100%" }}
                        allowClear
                      />
                    </Form.Item>

                    <Form.List name="mapCodeList">
                      {(fields, { add, remove }) => (
                        <div>
                          {fields.map((field) => (
                            <div
                              key={field.key}
                              style={{
                                display: "grid",
                                gridTemplateColumns: "200px 1fr 28px",
                                gap: 8,
                                alignItems: "center",
                                marginBottom: 12,
                              }}
                            >
                              <div
                                style={{
                                  textAlign: "right",
                                  paddingRight: 8,
                                  fontWeight: 500,
                                }}
                              >
                                Parcel #
                              </div>
                              <Form.Item
                                name={field.name}
                                style={{ marginBottom: 0 }}
                              >
                                <Input placeholder="Parcel #" allowClear />
                              </Form.Item>
                              <Button
                                type="text"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => remove(field.name)}
                                disabled={fields.length <= 1}
                              />
                            </div>
                          ))}
                          <Button
                            type="dashed"
                            icon={<PlusOutlined />}
                            onClick={() => add("")}
                            style={{ marginBottom: 12 }}
                          >
                            Add Parcel
                          </Button>
                        </div>
                      )}
                    </Form.List>

                    <Form.Item name="sqFeet" label="Build Square Footage">
                      <Input placeholder="Build Square Footage" allowClear />
                    </Form.Item>
                  </SectionCard>
                </div>

                {/* ================= 3. Management Information ================= */}
                <SectionCard
                  icon={<TeamOutlined />}
                  title="Management Information"
                >
                  <Form.Item
                    name="companyId"
                    label="Property Management Company"
                    rules={[
                      { required: true, message: "Please select Company" },
                    ]}
                  >
                    <Select
                      placeholder="Select Company"
                      loading={companySelect.loading}
                      options={companySelect.options}
                      {...searchProps}
                    />
                  </Form.Item>

                  <Form.Item
                    name="groupId"
                    label="Geography Group"
                    rules={[{ required: true, message: "Please select Group" }]}
                  >
                    <Select
                      placeholder="Select Group"
                      loading={groupSelect.loading}
                      options={groupSelect.options}
                      {...searchProps}
                    />
                  </Form.Item>

                  {commonFn.formInputNumberOptionalDoc({
                    name: "manFlatAmt",
                    label: "Management Flat Rate",
                    form,
                    isNoRequired: true,
                  })}

                  <Form.Item
                    name="manPct"
                    label="Management Fee %"
                    rules={[
                      {
                        required: true,
                        message: "Please enter Management Fee %",
                      },
                    ]}
                  >
                    <Input placeholder="Management Fee %" allowClear />
                  </Form.Item>

                  <Form.Item name="line2" label="Manager Info">
                    <Input placeholder="Manager Info" allowClear />
                  </Form.Item>

                  <Form.Item name="rentType" label="Rent Type">
                    <Select
                      placeholder="Select Rent Type"
                      options={RENT_TYPE_OPTIONS}
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item
                    name="phone"
                    label="Phone"
                    rules={[{ validator: commonFn.usPhoneValidator }]}
                  >
                    <Input placeholder="Phone" allowClear />
                  </Form.Item>

                  <Form.Item
                    name="manOnSite"
                    label="On Site Manager"
                    rules={[
                      {
                        required: true,
                        message: "Please enter On Site Manager",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select"
                      options={MAN_ON_SITE_OPTIONS}
                      allowClear
                    />
                  </Form.Item>

                  <Form.Item name="manFirstName" label="Manager First Name">
                    <Input placeholder="Manager First Name" allowClear />
                  </Form.Item>

                  <Form.Item name="manLastName" label="Manager Last Name">
                    <Input placeholder="Manager Last Name" allowClear />
                  </Form.Item>
                </SectionCard>
              </Flex>
            </Form>
          </Spin>
        </div>
      </Modal>
    </ConfigProvider>
  );
});

export default EditModal;
